import { useEffect, useMemo, useState } from 'react'
import RadioIcon from '@/CustomIcons/RadioIcon'
import { Card, cardXPad } from '../../Card'
import { CardHeader } from '../../CardHeader'
import { SessionTypeInput } from '@/components/SessionTypeInput'
import { ApiRoomRequest, getRoomName, RoomContextState, useRoomContext } from '@/RoomContext'
import { TextInput } from '@/UIComponents2/TextInput'
import EyeIcon from '@/CustomIcons/EyeIcon'
import EyeClosedIcon from '@/CustomIcons/EyeClosedIcon'
import { Checkbox } from '@/UIComponents2/Checkbox'
import UserIcon from '@/CustomIcons/UserIcon'
import { Alert } from '@/UIComponents2/Alert'
import { useTranslation } from 'react-i18next'
import { debounce } from 'lodash'
import { useMutate } from 'restful-react'
import { Field } from '../../Field'
import useDebounce from '@/hooks/useDebounce'
import { createCustomJoinLink } from '@/Logic'
import { useUserContext } from '@/UserContext'
import { useAppContext } from '@/AppContext'
import useRoomViews from '@/services/useRoomViews'
import { Separator } from '../../Separator'
import { Button } from '@/UIComponents2/Button'
import useRoomActions from '@/services/useRoomActions'

export function SessionSection() {
    const { t } = useTranslation()
    const [userState] = useUserContext()
    const [roomState, roomDispatch] = useRoomContext()
    const [appState, appDispatch] = useAppContext()
    const { canTogglePermanentRoom, isMaxPermanentRoomCount } = useRoomViews()
    const { hostControlRemoteMuteAllUsers } = useRoomActions()

    const { mutate: updateRoomApi, loading } = useMutate({
        verb: 'PATCH',
        path: `/v1/rooms/${roomState.roomUrn || roomState.preRoomUrn || ''}`,
    })

    const isHost = roomState.isHost

    const permanent = roomState.permanent ?? false
    const camp = roomState.isSubroomsEnabled ?? false
    const livestream = roomState.isListeningSession ?? false

    const canToggleCamp = userState.profile?.policy?.roomTierPolicy.allowSubrooms ?? false
    const allowLivestream = userState.profile?.policy?.allowCreateListeningSessions ?? false

    const handleSessionKindChange = (
        permanent: boolean,
        camp: boolean,
        livestream: boolean,
        apiDiff: Partial<ApiRoomRequest>,
        contextDiff: Partial<RoomContextState>
    ) => {
        if (permanent && isMaxPermanentRoomCount) {
            const msg = t(
                'You cannot create more permanent sessions. Please remove existing permanent sessions to create more.'
            )
            appDispatch({
                type: 'new_message_dialog',
                value: {
                    id: 'cannot_create_more_permanent_sessions',
                    text: msg,
                },
            })
            return
        }

        if (!camp && roomState.subrooms?.length > 0) {
            const msg = t('You cannot turn off songwriting camp mode while there are studios')
            appDispatch({
                type: 'new_message_dialog',
                value: {
                    id: 'cannot_disable_camp_mode',
                    text: msg,
                },
            })
            return
        }

        if (!livestream) {
            const hasViewer = Object.entries(roomState.peers).some(([_, p]) => p.clientType === 'listenonly')
            if (hasViewer) {
                const msg = t('You cannot turn off livestream mode while there are viewers.')
                appDispatch({
                    type: 'new_message_dialog',
                    value: {
                        id: 'cannot_disable_livestream_mode',
                        text: msg,
                    },
                })
                return
            }
        }

        roomDispatch({ type: 'update_room_context', value: contextDiff })
        updateRoomApi(apiDiff)
    }

    const handleHostGalleryPrefsChange = (val: boolean) => {
        appDispatch({
            type: 'update_app_settings',
            value: {
                applyHostGalleryPrefsToAllParticipants: val,
            },
        })

        updateRoomApi({ applyHostGalleryPrefsToAllParticipants: val })
    }

    const handleParticipantsJoinMutedChange = (val: boolean) => {
        const allParticipantsJoinMuted = !roomState.allParticipantsJoinMuted
        roomDispatch({
            type: 'update_room_context',
            value: { allParticipantsJoinMuted },
        })

        updateRoomApi({ allParticipantsJoinMuted })
    }

    const handleResetAfterSessionChange = (val: boolean) => {
        const next = roomState.permanent && val
        roomDispatch({
            type: 'update_room_context',
            value: { resetAfterSession: next },
        })
        updateRoomApi({
            resetAfterSession: next,
        })
    }

    return (
        <Card className="flex flex-col gap-[40px] px-0 md:px-0">
            <CardHeader icon={RadioIcon} title="Session Settings" className={cardXPad} />
            <div className="flex flex-col gap-[26px]">
                <Field title={t('Session Type')}>
                    <SessionTypeInput
                        permanent={permanent}
                        camp={camp}
                        livestream={livestream}
                        onChange={handleSessionKindChange}
                        permanentLocked={!canTogglePermanentRoom && isHost}
                        campLocked={!canToggleCamp && isHost}
                        livestreamLocked={!allowLivestream && isHost}
                        disabled={!isHost}
                    />
                </Field>
                <Separator />
                <SessionNameField updateRoom={updateRoomApi} />
                {isHost && userState.profile?.policy?.allowCustomSessionCode && (
                    <CustomSessionCodeField updateRoom={updateRoomApi} />
                )}
                <Field title={t('Session Host')} className="items-center">
                    <div className="border-muse-toplit-low shadow-glow rounded-[12px] py-[7px] px-3 inline-flex gap-2 items-center font-light text-muse-fg/65">
                        <UserIcon style={{ fontSize: '1.125rem' }} /> {roomState.host?.username}
                    </div>
                </Field>
                <PasswordField updateRoom={updateRoomApi} />
                <Separator />
                <Field title={t('Additional Settings')}>
                    <div className="flex flex-col gap-6">
                        <Checkbox
                            label={t('Apply hosts video gallery preferences to all participants')}
                            checked={appState.appSettings.applyHostGalleryPrefsToAllParticipants}
                            onChange={(v) => handleHostGalleryPrefsChange(v)}
                            labelClassName="font-light"
                            disabled={!isHost}
                        />
                        <Checkbox
                            label={t('Participants join muted by default')}
                            checked={roomState.allParticipantsJoinMuted}
                            onChange={(v) => handleParticipantsJoinMutedChange(v)}
                            labelClassName="font-light"
                            disabled={!isHost}
                        />
                        {roomState.permanent && (
                            <Checkbox
                                label={t('Clear chat after session')}
                                checked={roomState.resetAfterSession ?? false}
                                onChange={(v) => handleResetAfterSessionChange(v)}
                                labelClassName="font-light"
                                disabled={!isHost}
                            />
                        )}
                        {roomState.isHost && (
                            <div>
                                <Button
                                    variant="secondary"
                                    onClick={() => {
                                        hostControlRemoteMuteAllUsers()
                                    }}
                                >
                                    {t('Mute all users remotely')}
                                </Button>
                            </div>
                        )}
                    </div>
                </Field>
            </div>
        </Card>
    )
}

type SessionNameFieldProps = {
    updateRoom: (data: Partial<ApiRoomRequest>) => Promise<any>
}
function SessionNameField(props: SessionNameFieldProps) {
    const [roomState, roomDispatch] = useRoomContext()
    const [sessionName, setSessionName] = useState(roomState.name || '')
    const sessionName_debounced = useDebounce<string>(sessionName, 250, { ignoreInitialUpdate: true })

    useEffect(() => {
        if (sessionName_debounced === roomState.name) {
            return
        }

        roomDispatch({
            type: 'update_room_context',
            value: { name: sessionName_debounced },
        })
        props.updateRoom({
            name: sessionName_debounced,
        })
    }, [sessionName_debounced])

    return (
        <Field title="Session Name" className="items-center">
            <TextInput
                placeholder={getRoomName(roomState)}
                onChange={(v) => setSessionName(v)}
                value={sessionName}
                disabled={!roomState.isHost}
            />
        </Field>
    )
}

type CustomSessionCodeFieldProps = {
    updateRoom: (data: Partial<ApiRoomRequest>) => Promise<any>
}
function CustomSessionCodeField(props: CustomSessionCodeFieldProps) {
    const [roomState, roomDispatch] = useRoomContext()
    const [error, setError] = useState<string>('')
    const [customJoinCode, setCustomJoinCode] = useState<string>(
        roomState.parentRoom?.customJoinCode || roomState.customJoinCode || ''
    )

    const customJoinCode_debounced = useDebounce<string>(customJoinCode, 500, { ignoreInitialUpdate: true })
    useEffect(() => {
        if (!roomState.isHost) {
            return
        }
        setError('')
        if (customJoinCode_debounced === (roomState.customJoinCode ?? '')) {
            return
        }

        const customJoinCodeNext = (customJoinCode || '').trim()

        if (customJoinCodeNext.length !== 0 && customJoinCodeNext.length <= 8) {
            setError('Requires at least 8 characters')
            return
        }

        props
            .updateRoom({
                customJoinCode: customJoinCodeNext,
            })
            .then((_response) => {
                roomDispatch({
                    type: 'update_room_context',
                    value: {
                        customJoinCode: customJoinCodeNext,
                        customJoinLink: createCustomJoinLink(customJoinCodeNext),
                    },
                })
            })
            .catch((e) => {
                if (e?.data?.code === 'duplicate_custom_join_code') {
                    setError('This session code is already in use')
                } else {
                    setError('Server error: ' + e?.data?.code)
                }
            })
    }, [customJoinCode_debounced])

    return (
        <Field title="Custom Session Code" className="items-center">
            <TextInput
                placeholder="muse-camp-2025"
                onChange={(v) => setCustomJoinCode(v)}
                value={customJoinCode}
                error={error}
            />
        </Field>
    )
}

type PasswordFieldProps = {
    updateRoom: (data: Partial<ApiRoomRequest>) => void
}
function PasswordField(props: PasswordFieldProps) {
    const { t } = useTranslation()
    const [roomState, roomDispatch] = useRoomContext()
    const [showPassword, setShowPassword] = useState(false)

    const isHost = roomState.isHost
    const isSubroom = !!roomState.parentRoom

    const handlePasswordChange = useMemo(
        () =>
            debounce((v: string) => {
                roomDispatch({
                    type: 'update_room_context',
                    value: { password: v },
                })
                props.updateRoom({
                    password: v,
                })
            }, 1000),
        [props, roomDispatch]
    )

    useEffect(() => {
        return () => handlePasswordChange.cancel()
    }, [handlePasswordChange])

    const renderContent = () => {
        if (!isHost) {
            return (
                <Alert variant="solid" level="warning">
                    {t('Only the session host can set the password.')}
                </Alert>
            )
        }

        if (isSubroom) {
            return (
                <Alert variant="solid" level="warning">
                    {t('This is a breakout session. Password may only be set on the main room.')}
                </Alert>
            )
        }

        return (
            <TextInput
                placeholder={'(' + t('optional') + ')'}
                initialValue={roomState.password ?? ''}
                onChange={(v) => handlePasswordChange(v)}
                type={showPassword ? 'text' : 'password'}
                rightControl={
                    <button
                        className="cursor-pointer opacity-50"
                        onClick={(e) => {
                            e.preventDefault()
                            setShowPassword(!showPassword)
                        }}
                    >
                        {showPassword ? <EyeIcon /> : <EyeClosedIcon />}
                    </button>
                }
            />
        )
    }

    return (
        <Field title="Session Password" className="items-center">
            {renderContent()}
        </Field>
    )
}
